import type { ReactNode } from 'react'
import { useI18n } from '../hooks/useI18n'
import { env } from '../lib/env'
import TabBar, { type TabKey } from './TabBar'
import logo from '../assets/logo-tp.png'

/**
 * The frame every screen sits in. Spacing is copied from the mini program's
 * app.css, value for value, because "same design" fails on details like a
 * missing 1px border far more often than on colour:
 *
 *   .wx-bar   padding: 14px 16px 12px
 *             border-bottom: 1px solid rgba(201,169,110,0.1)
 *   .wx-logo  96 x 38
 *   .pad      padding: 20px 20px 0
 *
 * The tab bar stays visible on EVERY screen, login and onboarding included —
 * hiding it is what trapped people in the login flow and failed WeChat review.
 */
export default function PageLayout({
  active, onTab, onHome, children
}: {
  active: TabKey
  onTab: (tab: TabKey) => void
  onHome?: () => void
  children: ReactNode
}) {
  const { toggle, lang } = useI18n()

  return (
    // Column layout so the ICP line is pushed to the bottom on a short page and
    // simply follows the content on a long one.
    <div className="mx-auto flex min-h-full max-w-app flex-col pb-tabbar">
      <header
        className="flex shrink-0 items-center justify-between border-b border-[rgba(201,169,110,0.1)] bg-bg"
        style={{ padding: '14px 16px 12px', paddingTop: 'calc(14px + env(safe-area-inset-top))' }}
      >
        {/* 620x265 source; fixed box, object-contain keeps the ratio. */}
        <button type="button" onClick={() => onHome && onHome()} aria-label="Home" className="flex min-w-0 items-center">
          <img src={logo} alt="Top Chopsticks" className="h-[38px] w-[96px] shrink-0 object-contain" />
        </button>
        <button
          type="button"
          onClick={toggle}
          className="rounded-pill border border-gold-dim px-3 py-1 text-xs text-gold"
        >
          {lang === 'zh' ? 'EN' : '中文'}
        </button>
      </header>

      {/* .pad — 20px sides, 20px top, 0 bottom. */}
      <main className="flex-1 px-5 pt-5">{children}</main>

      {env.icpNumber ? (
        <footer className="shrink-0 px-5 pb-4 pt-6 text-center">
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noreferrer"
            className="text-[10px] leading-none text-muted-2"
          >
            {env.icpNumber}
          </a>
        </footer>
      ) : null}

      <TabBar active={active} onTab={onTab} />
    </div>
  )
}
